import TurnstileWidget from "@/ui/components/TurnstileWidget";
import { useState, useEffect, useRef } from "preact/hooks";
import { AsYouType, parsePhoneNumberFromString } from "libphonenumber-js";
import { countries, getEmojiFlag } from "countries-list";
import { Button } from "@/ui/shadcn/button";
import { isDevHost } from "@/lib/web";
import { uiLangIsRtl } from "@/l11n/l11n";
import { doLog } from "@/lib/globals";
import { localStorageGet, STORAGE_KEYS } from "@/lib/storage";

const COUNTRIES = Object.entries(countries)
  .map(([code, data]) => ({
    code,
    name: data.name,
    callingCode: data.phone,
    flag: getEmojiFlag(code),
  }))
  .filter((country) => country.callingCode)
  .sort((a, b) => a.name.localeCompare(b.name));

export default function PhoneNumberInput({
  strings,
  onSubmit,
  onFocus,
  onBlur,
  useWhatsApp,
}) {
  // MARK: State
  const [value, setValue] = useState("");
  const [isValid, setIsValid] = useState(false);
  const [validParsedPhoneNumber, setValidParsedPhoneNumber] = useState(null);
  const [flagData, setFlagData] = useState(null);
  const [showCountryPicker, setShowCountryPicker] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showTurnstileModal, setShowTurnstileModal] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);

  // MARK: Refs
  const inputRef = useRef();
  const countryPickerRef = useRef();
  const searchInputRef = useRef();

  const isRtl = uiLangIsRtl();

  // MARK: Initial Country
  // Get country from query params if expectedCountry is null
  const getInitialCountry = () => {
    const cc = localStorageGet(STORAGE_KEYS.countryCode);
    if (cc) {
      if (doLog()) console.log("getInitialCountry found country in session storage:", cc);
      return cc;
    }
    const urlParams = new URLSearchParams(window.location.search);
    const cnParam = urlParams.get("CN");
    return cnParam ? cnParam.toUpperCase() : "US";
  };

  // NB: must be after getInitialCountry
  const [selectedCountry, setSelectedCountry] = useState(() => {
    const countryCode = getInitialCountry();
    return COUNTRIES.find((c) => c.code === countryCode) || COUNTRIES[0];
  });

  // MARK: Filter Countries
  // Filter countries based on search term
  const filteredCountries = COUNTRIES.filter((country) => {
    const searchLower = searchTerm.toLowerCase().trim();
    const nameLower = country.name.toLowerCase();
    const codeLower = country.code.toLowerCase();
    const callingCodeSearch = searchLower.startsWith("+")
      ? searchLower.substring(1)
      : searchLower;

    // Convert calling code array to string for comparison
    const callingCodeStr = country.callingCode[0]?.toString() || "";

    // Check for exact two-letter country code match
    if (searchLower.length === 2 && codeLower === searchLower) {
      return true;
    }

    return (
      nameLower.includes(searchLower) ||
      codeLower.includes(searchLower) ||
      callingCodeSearch === callingCodeStr || // Match exact calling code
      (callingCodeSearch.length > 0 && callingCodeStr.includes(callingCodeSearch))
    ); // Also match partial calling code
  }).sort((a, b) => {
    const searchLower = searchTerm.toLowerCase().trim();
    const callingCodeSearch = searchLower.startsWith("+")
      ? searchLower.substring(1)
      : searchLower;

    // Exact calling code match gets highest priority
    const aCallingCode = a.callingCode[0]?.toString() || "";
    const bCallingCode = b.callingCode[0]?.toString() || "";

    if (callingCodeSearch === aCallingCode && callingCodeSearch !== bCallingCode)
      return -1;
    if (callingCodeSearch !== aCallingCode && callingCodeSearch === bCallingCode)
      return 1;

    // Exact country code match gets second priority
    if (searchLower.length === 2) {
      const aCode = a.code.toLowerCase();
      const bCode = b.code.toLowerCase();
      if (aCode === searchLower && bCode !== searchLower) return -1;
      if (aCode !== searchLower && bCode === searchLower) return 1;
    }

    // Default to alphabetical order
    return a.name.localeCompare(b.name);
  });

  // MARK: Effects

  // Focus search input when dropdown opens
  useEffect(() => {
    if (showCountryPicker && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [showCountryPicker]);

  // Close country picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (countryPickerRef.current && !countryPickerRef.current.contains(event.target)) {
        setShowCountryPicker(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // MARK: Validate
  useEffect(() => {
    if (value == validParsedPhoneNumber) {
      // If the value is already valid, don't validate again
      return;
    }
    let phoneToValidate = value;

    // If number doesn't start with +, add the selected country's code
    if (!phoneToValidate.startsWith("+")) {
      phoneToValidate = `+${selectedCountry.callingCode}${phoneToValidate}`;
    }
    const phone = parsePhoneNumberFromString(phoneToValidate, selectedCountry.code);
    const valid = phone ? phone.isValid() : false;
    setIsValid(valid);
    if (valid && phone.country) {
      setFlagData(getEmojiFlag(selectedCountry.code));
      setValidParsedPhoneNumber(phone.number);
    } else {
      setFlagData(null);
      setValidParsedPhoneNumber(null);
    }
  }, [value, selectedCountry.code]);

  // MARK: Handle Input
  const handleChange = (e) => {
    let v = e.currentTarget.value;

    // Phone mode handling
    if (v.startsWith("+")) {
      const digitsOnly = v.replace(/\D/g, "");

      if (digitsOnly.length >= 2) {
        if (digitsOnly.startsWith("1")) {
          const usCountry = COUNTRIES.find((country) => country.code === "US");
          if (usCountry && usCountry.code !== selectedCountry.code) {
            setSelectedCountry(usCountry);
            const remaining = v.substring(2);
            setValue(remaining.trim());
            return;
          }
        } else {
          let matchingCountry = COUNTRIES.find((country) =>
            digitsOnly.startsWith(country.callingCode)
          );

          if (matchingCountry) {
            if (matchingCountry.code !== selectedCountry.code) {
              setSelectedCountry(matchingCountry);
              setValue("");
              return;
            }

            if (digitsOnly.length > matchingCountry.callingCode.length) {
              v = digitsOnly.slice(matchingCountry.callingCode.length);
              const formatter = new AsYouType(matchingCountry.code);
              const formatted = formatter.input(`+${matchingCountry.callingCode}${v}`);
              if (formatter.isValid()) {
                v = formatted.split(" ").slice(1).join(" ");
              } else {
                v = v.replace(/(\d{3})/g, "$1 ").trim();
              }
              setValue(v);
              return;
            }
          }
        }
      }
      setValue(v);
      return;
    }

    v = v.replace(/\D/g, "");
    if (v.length > 0) {
      const formatter = new AsYouType(selectedCountry.code);
      const formatted = formatter.input(`+${selectedCountry.callingCode}${v}`);
      if (formatter.isValid()) {
        v = formatted.split(" ").slice(1).join(" ");
      } else {
        v = v.replace(/(\d{3})/g, "$1 ").trim();
      }
    }

    setValue(v);
  };

  // MARK: Submit
  const handleSend = (e) => {
    e.preventDefault();
    if (doLog()) console.log("handleSend raw value:", value);
    if (isValid && value.trim()) {
      let finalValue;

      if (!isDevHost) {
        setShowTurnstileModal(true);
        return;
      }
      finalValue = validParsedPhoneNumber;

      if (doLog()) console.log("handleSend final value:", finalValue);
      onSubmit({
        value: finalValue,
        country: selectedCountry.code,
        turnstileToken,
        isWhatsApp: useWhatsApp,
      });
      setValue(""); // Clear the input after submitting
    }
  };

  // MARK: Country Select
  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
    setShowCountryPicker(false);

    // If we have a phone number, reformat it with the new country code
    if (value) {
      const digitsOnly = value.replace(/\D/g, "");
      const newValue = `+${country.callingCode}${digitsOnly}`;
      setValue(new AsYouType(country.code).input(newValue));
    }
  };

  // MARK: Clear
  const handleClear = () => {
    setValue("");
    setIsValid(false);
    setFlagData(null);
    inputRef.current.focus();
  };

  // MARK: Turnstile
  const handleTurnstileSuccess = (token) => {
    setTurnstileToken(token);
    setShowTurnstileModal(false);
    // Now we can submit with the token
    if (isValid && value.trim()) {
      let finalValue = validParsedPhoneNumber;
      onSubmit({
        value: finalValue,
        country: selectedCountry.code,
        turnstileToken: token,
        isWhatsApp: useWhatsApp,
      });
      setValue(""); // Clear the input after submitting
    }
  };

  const handleTurnstileError = (msg, error) => {
    if (doLog()) console.error("Turnstile error in EmailPhoneInput:", msg, error);
    setShowTurnstileModal(false);
    // TODO: something here?
  };

  // MARK: Render
  return (
    <form onSubmit={handleSend} className="w-full flex flex-col gap-2">
      <div className="flex items-center">
        {/* Country Picker  */}
        <div
          ref={countryPickerRef}
          style={{
            position: "relative",
          }}
        >
          <button
            type="button"
            onClick={() => setShowCountryPicker(!showCountryPicker)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.25rem",
              padding: "0.5rem",
              border: "1px solid #ccc",
              background: "white",
              borderRadius: "4px",
              cursor: "pointer",
              height: "40px",
              boxSizing: "border-box",
            }}
          >
            <span>{selectedCountry.flag}</span>
            <span>+{selectedCountry.callingCode}</span>
          </button>

          {showCountryPicker && (
            <div
              style={{
                position: "absolute",
                bottom: "100%",
                left: 0,
                background: "white",
                border: "1px solid #ccc",
                borderRadius: "4px",
                maxHeight: "160px",
                overflowY: "auto",
                zIndex: 1000,
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                width: "calc(100vw - 20px)",
                marginBottom: "4px",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <div
                style={{
                  padding: "0.5rem",
                  borderBottom: "1px solid #eee",
                  flexShrink: 0,
                }}
              >
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Country" // TODO: localize
                  value={searchTerm}
                  onInput={(e) => setSearchTerm(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "0.5rem",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    fontSize: "0.9rem",
                    WebkitAppearance: "none",
                  }}
                  autoComplete="off"
                  autoFocus
                />
              </div>
              <div style={{ overflowY: "auto", flexGrow: 1 }}>
                {filteredCountries.map((country) => (
                  <button
                    key={country.code}
                    type="button"
                    onClick={() => handleCountrySelect(country)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                      padding: "0.5rem",
                      width: "100%",
                      border: "none",
                      background: "transparent",
                      cursor: "pointer",
                      textAlign: "left",
                      borderBottom: "1px solid #eee",
                      height: "40px",
                    }}
                  >
                    <span style={{ minWidth: "1.5em" }}>{country.flag}</span>
                    <span style={{ minWidth: "2.5em" }}>+{country.callingCode}</span>
                    <span>{country.name}</span>
                  </button>
                ))}
                {filteredCountries.length === 0 && (
                  <div style={{ padding: "1rem", textAlign: "center", color: "#666" }}>
                    No countries found
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Text Input Field */}
        <div className="flex-1 relative">
          <input
            key="phone_entry"
            ref={inputRef}
            type="tel"
            value={value}
            placeholder={strings.input_placeholder_phone}
            onInput={handleChange}
            onFocus={() => {
              if (doLog()) console.log("onFocus PhoneNumberInput");
              if (onFocus) onFocus();
            }}
            onBlur={() => {
              if (doLog()) console.log("onBlur PhoneNumberInput");
              if (onBlur) onBlur();
            }}
            onTouchMove={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                handleSend(e);
              }
            }}
            aria-invalid={value ? !isValid : undefined}
            style={{
              width: "100%",
              padding: "0.5rem",
              border: `1px solid ${!value ? "#ccc" : isValid ? "green" : "red"}`,
              borderRadius: "4px",
              fontSize: "1rem",
              height: "40px",
              boxSizing: "border-box",
            }}
            className={`w-full border-none outline-none ring-0 focus:ring-0 focus:border-none focus:outline-none focus-visible:ring-0 focus-visible:border-none focus-visible:outline-none px-2 py-2 text-lg bg-transparent shadow-none ${
              isRtl ? "text-right" : "text-left"
            }`}
          />

          {value && !value.startsWith("+") && (
            <button
              onClick={handleClear}
              style={{
                position: "absolute",
                [isRtl ? "left" : "right"]: "0.75rem",
                top: "50%",
                transform: "translateY(-50%)",
                border: "none",
                background: "transparent",
                fontSize: "1.2rem",
                cursor: "pointer",
                lineHeight: 1,
                zIndex: 1,
                padding: 0,
              }}
              aria-label="Clear"
            >
              ×
            </button>
          )}

          {flagData && !isValid && (
            <span
              style={{
                position: "absolute",
                right: "0.75rem",
                top: "50%",
                transform: "translateY(-50%)",
                fontSize: "1.2em",
                zIndex: 1,
              }}
            >
              {flagData}
            </span>
          )}
        </div>

        {/* Submit Button */}
        <Button
          type="submit"
          variant="taalia_std"
          disabled={!isValid || !value.trim()}
          style={{
            height: "40px",
            boxSizing: "border-box",
          }}
        >
          {strings.submit}
        </Button>
      </div>

      {/* Turnstile Modal */}
      {showTurnstileModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              padding: "2rem",
              borderRadius: "8px",
              maxWidth: "90%",
              width: "400px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <TurnstileWidget
              onSuccess={handleTurnstileSuccess}
              onError={handleTurnstileError}
            />
          </div>
        </div>
      )}
    </form>
  );
}
